import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import {
  Box,
  Card,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  MenuItem,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

import DataTable from "@/components/mui/DataTable";

export default function CompanyUsers() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "company_user",
  });

  useEffect(() => {
    fetchCompanyAndUsers();
  }, [id]);

  const fetchCompanyAndUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/companies/${id}`);
      setCompany(response.data);
      setUsers(response.data.users || []);
    } catch (error) {
      toast.error("Erro ao carregar dados");
      navigate("/admin/companies");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        password: "",
        role: user.role,
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: "",
        email: "",
        password: "",
        role: "company_user",
      });
    }
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingUser(null);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        // Editar usuário
        const dataToSend = { ...formData, company_id: id };
        if (!formData.password) delete dataToSend.password;

        await axios.put(`${API}/users/${editingUser.id}`, dataToSend);
        toast.success("Usuário atualizado!");
      } else {
        // Criar usuário
        await axios.post(`${API}/users`, { ...formData, company_id: id });
        toast.success("Usuário criado!");
      }
      handleCloseDialog();
      fetchCompanyAndUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || "Erro ao salvar usuário");
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Tem certeza que deseja excluir este usuário?")) return;

    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success("Usuário excluído!");
      fetchCompanyAndUsers();
    } catch (error) {
      toast.error("Erro ao excluir usuário");
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // ===== columns do DataTable (mesmo padrão da tela Users) =====
  const columns = [
    {
      id: "name",
      label: "Nome",
      render: (user) => (
        <Typography fontWeight={600}>{user.name}</Typography>
      ),
    },
    {
      id: "email",
      label: "Email",
    },
    {
      id: "role",
      label: "Perfil",
      render: (user) => (
        <Chip
          label={user.role === "company_supervisor" ? "Supervisor" : "Usuário"}
          color={user.role === "company_supervisor" ? "primary" : "default"}
          size="small"
        />
      ),
    },
    {
      id: "status",
      label: "Status",
      render: (user) => (
        <Chip
          label={user.active ? "Ativo" : "Inativo"}
          color={user.active ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      id: "actions",
      label: "Ações",
      align: "center",
      render: (user) => (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => handleOpenDialog(user)}
            data-testid={`edit-company-user-${user.id}`}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(user.id)}
            data-testid={`delete-company-user-${user.id}`}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box data-testid="company-users-page">
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Usuários - {company?.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gerencie os usuários desta empresa
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          <Tooltip title="Voltar para detalhes" arrow>
            <IconButton
              onClick={() => navigate(`/admin/companies/${id}`)}
              size="small"
              sx={{
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
              }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Novo Usuário
          </Button>
        </Box>
      </Box>

      {/* Lista usando DataTable */}
      <Card
        sx={{
          borderRadius: 2,
          boxShadow: "0 10px 40px rgba(15,23,42,0.04)",
          border: "1px solid #e2e8f0",
        }}
      >
        {users.length === 0 ? (
          <Box
            sx={{
              py: 4,
              px: 3,
              textAlign: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Nenhum usuário cadastrado
            </Typography>
          </Box>
        ) : (
          <DataTable
            columns={columns}
            rows={users}
            loading={false}
            getRowId={(row) => row.id}
            getRowTestId={(row) => `company-user-row-${row.id}`}
          />
        )}
      </Card>

      {/* Dialog */}
      <Dialog
        open={showDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingUser ? "Editar Usuário" : "Novo Usuário"}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nome"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Senha"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  required={!editingUser}
                  helperText={
                    editingUser
                      ? "Deixe em branco para manter a senha atual"
                      : ""
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  select
                  label="Perfil"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                >
                  <MenuItem value="company_supervisor">Supervisor</MenuItem>
                  <MenuItem value="company_user">Usuário</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button type="submit" variant="contained">
              {editingUser ? "Salvar" : "Criar"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}
